/**
 * Custom hook for managing cookie consent
 * Integrates with Google Consent Mode for GDPR/CCPA compliance
 */

import { useState, useEffect } from 'react';
import { updateConsentState } from '../utils/analytics';

export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'custom';

export interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
}

const CONSENT_STORAGE_KEY = 'haven_cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment this to force re-consent if needed
export const COOKIE_CONSENT_CHANGED_EVENT = 'haven-cookie-consent-changed';

interface StoredConsent {
  status: ConsentStatus;
  preferences: CookiePreferences;
  version: string;
  timestamp: number;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true, // Always enabled
  analytics: false,
  marketing: false,
};

/**
 * Get stored consent from localStorage
 */
export const getStoredConsent = (): StoredConsent | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const consent: StoredConsent = JSON.parse(stored);
    
    // Check if consent version matches (allows forcing re-consent)
    if (consent.version !== CONSENT_VERSION) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    return consent;
  } catch (error) {
    console.error('Error reading consent from localStorage:', error);
    return null;
  }
};

/**
 * Save consent to localStorage
 */
const saveConsent = (status: ConsentStatus, preferences: CookiePreferences): void => {
  if (typeof window === 'undefined') return;

  try {
    const consent: StoredConsent = {
      status,
      preferences,
      version: CONSENT_VERSION,
      timestamp: Date.now(),
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, {
        detail: consent,
      })
    );
  } catch (error) {
    console.error('Error saving consent to localStorage:', error);
  }
};

/**
 * Hook for managing cookie consent
 */
export const useCookieConsent = () => {
  // The site is prerendered to static HTML with no access to localStorage, so
  // the prerendered markup always reflects the 'pending' state. The first
  // client render MUST match that exactly or hydration mismatches (React
  // #418) for any returning visitor whose consent is already stored. So we
  // start from the SSR value and adopt the persisted consent after mount.
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [isInitialized] = useState(true);

  // Adopt any persisted consent once mounted, and sync Google Consent Mode.
  // This intentionally sets state in an effect: it's a post-hydration
  // reconciliation with an external store (localStorage), which can't be read
  // during render without breaking SSR hydration.
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration: adopt persisted consent after the SSR-matched first render
      setConsentStatus(stored.status);
      setPreferences(stored.preferences);
      if (stored.status === 'accepted' || stored.status === 'custom') {
        updateConsentState(stored.preferences.analytics, stored.preferences.marketing);
      } else {
        updateConsentState(false, false);
      }
    }
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    
    setConsentStatus('accepted');
    setPreferences(newPreferences);
    saveConsent('accepted', newPreferences);
    updateConsentState(true, true);
  };

  /**
   * Reject all non-essential cookies
   */
  const rejectAll = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    
    setConsentStatus('rejected');
    setPreferences(newPreferences);
    saveConsent('rejected', newPreferences);
    updateConsentState(false, false);
  };

  /**
   * Save custom preferences
   */
  const saveCustomPreferences = (customPreferences: Partial<CookiePreferences>) => {
    const newPreferences: CookiePreferences = {
      ...preferences,
      ...customPreferences,
      necessary: true, // Always keep necessary enabled
    };
    
    setConsentStatus('custom');
    setPreferences(newPreferences);
    saveConsent('custom', newPreferences);
    
    // Update Google Consent Mode with granular consent preferences
    updateConsentState(newPreferences.analytics, newPreferences.marketing);
  };

  /**
   * Check if consent has been given (accepted or custom)
   */
  const hasConsented = consentStatus === 'accepted' || consentStatus === 'custom';

  /**
   * Check if consent is pending (not yet given)
   */
  const isPending = consentStatus === 'pending';

  return {
    consentStatus,
    preferences,
    isInitialized,
    hasConsented,
    isPending,
    acceptAll,
    rejectAll,
    saveCustomPreferences,
  };
};
